<script setup>
import { computed, ref, watch } from 'vue'
import { Label } from '../../../../../../../resources/js/components/ui/label/index.js'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { useEventListener } from '@vueuse/core'

const emit = defineEmits(['submit'])

const selectedDate = ref('')
const selectedSlot = ref('')
const isSubmitting = ref(false)
const currentMonth = ref(new Date())
const activePane = ref('calendar')
const slotIndex = ref(0)

const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const formatDate = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const shiftDateByDays = (days) => {
  const base = selectedDate.value ? new Date(`${selectedDate.value}T00:00:00`) : new Date()
  base.setDate(base.getDate() + days)
  selectedDate.value = formatDate(base)
  currentMonth.value = new Date(base.getFullYear(), base.getMonth(), 1)
}

const getQuickDate = (type) => {
  const today = new Date()
  const date = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  if (type === 'tomorrow') {
    date.setDate(date.getDate() + 1)
  } else if (type === 'dayAfterTomorrow') {
    date.setDate(date.getDate() + 2)
  } else if (type === 'endOfWeek') {
    const day = date.getDay()
    const daysUntilFriday = (5 - day + 7) % 7
    date.setDate(date.getDate() + daysUntilFriday)
  } else if (type === 'nextMonday') {
    const day = date.getDay()
    const daysUntilMonday = (8 - day) % 7 || 7
    date.setDate(date.getDate() + daysUntilMonday)
  }

  return formatDate(date)
}

const setQuickDate = (type) => {
  const next = getQuickDate(type)
  selectedDate.value = next
  const date = new Date(`${next}T00:00:00`)
  currentMonth.value = new Date(date.getFullYear(), date.getMonth(), 1)
}

const baseSlots = [
  '09:00 AM',
  '09:30 AM',
  '10:00 AM',
  '10:30 AM',
  '11:00 AM',
  '01:00 PM',
  '01:30 PM',
  '02:00 PM',
  '02:30 PM',
]

const availableSlots = computed(() => {
  if (!selectedDate.value) return []
  const dayNumber = Number(selectedDate.value.split('-')[2] || 0)
  const offset = dayNumber % 3
  return baseSlots.filter((_, index) => (index + offset) % 2 === 0)
})

const monthLabel = computed(() => {
  const date = currentMonth.value
  return date.toLocaleString('en-US', { month: 'long', year: 'numeric' })
})

const calendarDays = computed(() => {
  const date = currentMonth.value
  const year = date.getFullYear()
  const month = date.getMonth()
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const leadingEmpty = firstDay.getDay()
  const totalDays = lastDay.getDate()
  const days = []

  for (let i = 0; i < leadingEmpty; i += 1) {
    days.push({ label: '', date: null, isCurrentMonth: false })
  }

  for (let day = 1; day <= totalDays; day += 1) {
    const dayDate = new Date(year, month, day)
    days.push({
      label: String(day),
      date: formatDate(dayDate),
      isCurrentMonth: true,
    })
  }

  return days
})

const goToPrevMonth = () => {
  const date = currentMonth.value
  currentMonth.value = new Date(date.getFullYear(), date.getMonth() - 1, 1)
}

const goToNextMonth = () => {
  const date = currentMonth.value
  currentMonth.value = new Date(date.getFullYear(), date.getMonth() + 1, 1)
}

const selectCalendarDate = (date) => {
  if (!date) return
  selectedDate.value = date
}

const isToday = (date) => {
  if (!date) return false
  return date === formatDate(new Date())
}

const submitSchedule = async () => {
  if (!selectedDate.value || !selectedSlot.value) return

  isSubmitting.value = true
  emit('submit', {
    date: selectedDate.value,
    slot: selectedSlot.value,
  })
  isSubmitting.value = false
}

const selectSlotByIndex = (index) => {
  const slots = availableSlots.value
  if (!slots.length) return
  const nextIndex = Math.min(Math.max(index, 0), slots.length - 1)
  slotIndex.value = nextIndex
  selectedSlot.value = slots[nextIndex]
}

watch(selectedDate, () => {
  selectedSlot.value = ''
  slotIndex.value = 0
  activePane.value = 'calendar'
})

useEventListener(window, 'keydown', (event) => {
  const key = event.key
  if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'Backspace'].includes(key)) {
    event.preventDefault()
  } else {
    return
  }

  if (activePane.value === 'calendar') {
    if (key === 'ArrowLeft') shiftDateByDays(-1)
    if (key === 'ArrowRight') shiftDateByDays(1)
    if (key === 'ArrowUp') shiftDateByDays(-7)
    if (key === 'ArrowDown') shiftDateByDays(7)
    if (key === 'Enter' && selectedDate.value && availableSlots.value.length) {
      activePane.value = 'slots'
      selectSlotByIndex(0)
    }
  } else if (activePane.value === 'slots') {
    if (key === 'Escape' || key === 'Backspace') {
      activePane.value = 'calendar'
      return
    }
    const columns = 2
    if (key === 'ArrowLeft') selectSlotByIndex(slotIndex.value - 1)
    if (key === 'ArrowRight') selectSlotByIndex(slotIndex.value + 1)
    if (key === 'ArrowUp') selectSlotByIndex(slotIndex.value - columns)
    if (key === 'ArrowDown') selectSlotByIndex(slotIndex.value + columns)
  }
})
</script>

<template>
  <form class="grid gap-4" @submit.prevent="submitSchedule">
    <div class="text-sm font-medium text-muted-foreground">
      Customer Operation - Schedule Appointment
    </div>

    <div class="grid gap-2">
      <Label>Appointment Date</Label>
      <div class="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" @click="shiftDateByDays(-1)">
          ◀
        </Button>
        <div class="text-sm font-medium">
          {{ selectedDate || 'Select a date' }}
        </div>
        <Button type="button" variant="outline" size="sm" @click="shiftDateByDays(1)">
          ▶
        </Button>
      </div>
      <div class="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          :variant="selectedDate === getQuickDate('tomorrow') ? 'default' : 'outline'"
          @click="setQuickDate('tomorrow')"
        >
          Tomorrow
        </Button>
        <Button
          type="button"
          size="sm"
          :variant="selectedDate === getQuickDate('dayAfterTomorrow') ? 'default' : 'outline'"
          @click="setQuickDate('dayAfterTomorrow')"
        >
          Day After Tomorrow
        </Button>
        <Button
          type="button"
          size="sm"
          :variant="selectedDate === getQuickDate('endOfWeek') ? 'default' : 'outline'"
          @click="setQuickDate('endOfWeek')"
        >
          End of Week
        </Button>
        <Button
          type="button"
          size="sm"
          :variant="selectedDate === getQuickDate('nextMonday') ? 'default' : 'outline'"
          @click="setQuickDate('nextMonday')"
        >
          Next Monday
        </Button>
      </div>
      <div class="rounded-md border border-input p-3">
        <div class="mb-2 flex items-center justify-between">
          <Button type="button" variant="outline" size="sm" @click="goToPrevMonth">
            ◀
          </Button>
          <div class="text-sm font-medium">
            {{ monthLabel }}
          </div>
          <Button type="button" variant="outline" size="sm" @click="goToNextMonth">
            ▶
          </Button>
        </div>
        <div class="grid grid-cols-7 gap-1 text-xs text-muted-foreground">
          <div v-for="day in weekDays" :key="day" class="py-1 text-center">
            {{ day }}
          </div>
        </div>
        <div class="mt-1 grid grid-cols-7 gap-1">
          <button
            v-for="(day, index) in calendarDays"
            :key="`${day.date || 'empty'}-${index}`"
            type="button"
            :disabled="!day.date"
            class="h-9 rounded-md text-sm"
            :class="[
              day.date ? 'hover:bg-accent hover:text-accent-foreground' : 'opacity-0',
              selectedDate === day.date && 'bg-primary text-primary-foreground',
              isToday(day.date) && selectedDate !== day.date && 'border border-primary/30 text-primary'
            ]"
            @click="selectCalendarDate(day.date)"
          >
            {{ day.label }}
          </button>
        </div>
      </div>
    </div>

    <div class="grid gap-2">
      <div class="text-sm font-medium">Available Slots</div>
      <div v-if="availableSlots.length === 0" class="text-sm text-muted-foreground">
        Pick a date to see available times.
      </div>
      <div v-else class="grid grid-cols-2 gap-2">
        <Button
          v-for="(slot, index) in availableSlots"
          :key="slot"
          type="button"
          :variant="selectedSlot === slot ? 'default' : 'outline'"
          @click="
            activePane = 'slots';
            selectSlotByIndex(index);
          "
        >
          {{ slot }}
        </Button>
      </div>
    </div>

    <Button type="submit" :loading="isSubmitting" :disabled="!selectedDate || !selectedSlot">
      Confirm Appointment
    </Button>
  </form>
</template>

<style scoped>

</style>
